// src/components/pricingUnits/PricingUnitsTable.jsx
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { Stack, useTheme } from "@mui/material";

import useGetPricingUnits from "../../hooks/pricingUnits/useGetPricingUnits";
import useDeletePricingUnit from "../../hooks/pricingUnits/useDeletePricingUnit";
import useDeleteAllPricingUnits from "../../hooks/pricingUnits/useDeleteAllPricingUnits";
import useRestorePricingUnit from "../../hooks/pricingUnits/useRestorePricingUnit";
import useRestoreAllPricingUnits from "../../hooks/pricingUnits/useRestoreAllPricingUnits";
import useForceDeletePricingUnit from "../../hooks/pricingUnits/useForceDeletePricingUnit";
import useForceDeleteAllPricingUnits from "../../hooks/pricingUnits/useForceDeleteAllPricingUnits";

import useTableActions from "../../hooks/common/useTableActions";
import TableLayout from "../global/Table/TableLayout";
import PricingUnitsTableRow from "./Table/PricingUnitsTableRow";
import PricingUnitsTableColumns from "./Table/PricingUnitsTableColumns";
import ArchiveButton from "../global/Table/ArchiveButton";
import toast from "react-hot-toast";
import { usePermissions } from "../../auth/PermissionsContext";

const PricingUnitsTable = () => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng === "ar";
    const { has } = usePermissions();

    const perms = useMemo(
        () => ({
            list: has("pricingUnits_list"),
            create: has("pricingUnits_create"),
            edit: has("pricingUnits_edit"),
            delete: has("pricingUnits_delete"),
            forceDelete: has("pricingUnits_force_delete"),
            archiveList: has("pricingUnits_archive_list"),
            restore: has("pricingUnits_restore"),
            restoreAll: has("pricingUnits_restore-all-table"),
            deleteAll: has("pricingUnits_delete-all-table"),
            massForceDeleteAll: has("pricingUnits_mass_force_delete-all-table"),
        }),
        [has]
    );

    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    const isArchive = location.pathname.includes("/pricing-units/archive");

    // CRUD Hooks
    const { deletePricingUnit, isPending: isDelete } = useDeletePricingUnit();
    const { deleteAllPricingUnits, isPending: isDeleteAll } = useDeleteAllPricingUnits();
    const { restorePricingUnit, isPending: isRestore } = useRestorePricingUnit();
    const { restoreAllPricingUnits, isPending: isRestoreAll } = useRestoreAllPricingUnits();
    const { forceDeletePricingUnit, isPending: isForceDelete } = useForceDeletePricingUnit();
    const { forceDeleteAllPricingUnits, isPending: isForceDeleteAll } = useForceDeleteAllPricingUnits();

    // ✅ استخدام Custom Hook للإجراءات المشتركة
    const {
        page,
        perPage,
        search,
        sortBy,
        sortDir,
        selectedIds,
        menuAnchorEl,
        setSearch,
        handleRequestSort,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleEdit,
        handleMenuDelete,
        handleMenuRestore,
        handleBulkDelete,
        handlePageChange,
        handlePerPageChange,
        setSelectedIds,
    } = useTableActions({
        basePath: "/pricing-units",
        deleteSingle: deletePricingUnit,
        deleteAll: deleteAllPricingUnits,
        restoreSingle: restorePricingUnit,
        restoreAll: restoreAllPricingUnits,
        forceDeleteSingle: forceDeletePricingUnit,
        forceDeleteAll: forceDeleteAllPricingUnits,
        isArchive,
    });

    // Local State (specific to this table)
    const [statusFilter, setStatusFilter] = React.useState("");

    const filters = useMemo(
        () => ({
            search: search || undefined,
            is_active: statusFilter || undefined,
            sort_by: sortBy,
            sort_dir: sortDir,
            ...(isArchive ? { only_trashed: true } : {}),
        }),
        [search, statusFilter, sortBy, sortDir, isArchive]
    );

    const filterConfig = useMemo(
        () => [
            {
                type: "select",
                name: "is_active",
                label: { ar: "الحالة", en: "Status" },
                options: [
                    { value: "", label: { ar: "الكل", en: "All" } },
                    { value: "1", label: { ar: "نشط", en: "Active" } },
                    { value: "0", label: { ar: "معطل", en: "Disabled" } },
                ],
                value: statusFilter,
                onChange: setStatusFilter,
            },
        ],
        [statusFilter]
    );

    const handleClearAllFilters = () => {
        setStatusFilter("");
    };

    const { isLoading, error, pricingUnits, meta } = useGetPricingUnits(page, perPage, filters);

    const items = useMemo(() => pricingUnits?.items || [], [pricingUnits]);

    const rowMenuDisabled = isArchive
        ? !perms.restore && !perms.forceDelete
        : !perms.edit && !perms.delete;

    const renderRow = (params) => (
        <PricingUnitsTableRow
            key={params.item.id}
            pricingUnit={params.item}
            index={params.index}
            isDark={isDark}
            page={params.page}
            perPage={params.perPage}
            sortBy={params.sortBy}
            sortDir={params.sortDir}
            totalItems={items.length}
            selectedIds={selectedIds}
            onRowCheckboxClick={handleRowCheckboxClick}
            onMenuOpen={handleMenuOpen}
            isArchive={isArchive}
            menuDisabled={rowMenuDisabled}
        />
    );

    const bulkDeleteDisabled = isArchive ? !perms.massForceDeleteAll : !perms.deleteAll;

    const title = isArabic ? "وحدات التسعير" : "Pricing Units";
    const subtitle = isArchive
        ? isArabic
            ? "عرض وحدات التسعير المحذوفة (الأرشيف)"
            : "Archived pricing units (soft deleted)"
        : isArabic
            ? "إدارة وحدات التسعير المتاحة في النظام"
            : "Manage available pricing units in the system";

    const addButtonDisabled = isArchive
        ? !perms.restoreAll || selectedIds.length === 0
        : !perms.create;

    const addButtonLabel = isArchive
        ? isRestoreAll
            ? isArabic
                ? "جاري الاستعادة..."
                : "Restoring..."
            : isArabic
                ? "استعادة المحدد"
                : "Restore selected"
        : isArabic
            ? "إضافة وحدة تسعير"
            : "Create new pricing unit";

    const addButtonAction = isArchive
        ? () => {
            if (addButtonDisabled) {
                toast.error(
                    isArabic ? "ليس لديك صلاحية للاستعادة" : "No restore permission"
                );
                return;
            }

            if (isRestoreAll) return;

            if (!selectedIds.length) {
                toast.error(
                    isArabic
                        ? "الرجاء تحديد العناصر المراد استرجاعها أولاً"
                        : "Please select items to restore first"
                );
                return;
            }

            restoreAllPricingUnits({ items: selectedIds });
            setSelectedIds([]);
        }
        : () => {
            if (addButtonDisabled) {
                toast.error(
                    isArabic ? "ليس لديك صلاحية للإنشاء" : "No create permission"
                );
                return;
            }

            navigate("/pricing-units/create");
        };

    return (
        <>
            {!isArchive && (
                <Stack
                    direction="row"
                    justifyContent={isArabic ? "flex-start" : "flex-end"}
                    sx={{ mb: 1 }}
                >
                    <ArchiveButton
                        to="/pricing-units/archive"
                        visible={perms.archiveList}
                        disabled={!perms.archiveList}
                        archiveLabelAr="الأرشيف"
                        archiveLabelEn="Archive"
                    />
                </Stack>
            )}
            {isArchive && (
                <Stack
                    direction="row"
                    justifyContent={isArabic ? "flex-start" : "flex-end"}
                    sx={{ mb: 1 }}
                >
                    <ArchiveButton
                        to="/pricing-units"
                        visible={true}
                        archiveLabelAr="العودة للقائمة"
                        archiveLabelEn="Back to list"
                    />
                </Stack>
            )}
            <TableLayout
                title={title}
                subtitle={subtitle}
                search={search}
                onSearchChange={setSearch}
                addButtonLabel={addButtonLabel}
                onAddClick={addButtonAction}
                addButtonVisible={true}
                isLoading={isLoading}
                error={error}
                items={items}
                meta={meta}
                page={page}
                perPage={perPage}
                onPageChange={handlePageChange}
                onPerPageChange={handlePerPageChange}
                sortBy={sortBy}
                sortDir={sortDir}
                onRequestSort={handleRequestSort}
                columns={PricingUnitsTableColumns}
                renderRow={renderRow}
                menuAnchorEl={menuAnchorEl}
                openMenu={Boolean(menuAnchorEl)}
                onMenuOpen={handleMenuOpen}
                onMenuClose={handleMenuClose}
                onMenuDelete={handleMenuDelete}
                onMenuRestore={isArchive ? handleMenuRestore : undefined}
                onMenuEdit={!isArchive ? handleEdit : undefined}
                menuDisableEdit={!isArchive && !perms.edit}
                menuDisableRestore={isArchive && !perms.restore}
                menuDisableDelete={isArchive ? !perms.forceDelete : !perms.delete}
                isDeletingSingle={isArchive ? isRestore || isForceDelete : isDelete}
                selectedIds={selectedIds}
                onSelectAllClick={(e) => handleSelectAllClick(e, items)}
                onRowCheckboxClick={handleRowCheckboxClick}
                onBulkDelete={handleBulkDelete}
                bulkDeleteDisabled={bulkDeleteDisabled}
                isBulkDeleting={isArchive ? isForceDeleteAll : isDeleteAll}
                addButtonDisabled={addButtonDisabled}
                addButtonTooltip={
                    addButtonDisabled
                        ? isArabic
                            ? "لا تملك صلاحية تنفيذ هذا الإجراء"
                            : "You don't have permission"
                        : undefined
                }
                emptyLabelAr={isArchive ? "لا توجد وحدات تسعير في الأرشيف" : "لا توجد وحدات تسعير"}
                emptyLabelEn={isArchive ? "No archived pricing units" : "No pricing units"}
                loadErrorAr="حدث خطأ أثناء تحميل وحدات التسعير"
                loadErrorEn="Failed to load pricing units"
                filters={filterConfig}
                onClearAllFilters={handleClearAllFilters}
            />
        </>
    );
};

export default PricingUnitsTable;
